/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Tabs } from 'bold-ui'
import { PecSwitch, PrivateRoute, TabLink } from 'components/route'
import { useAcompanhamentoPreNatalQuery } from 'graphql/hooks.generated'
import { AtendimentoProfissional } from 'graphql/types.generated'
import { Fragment } from 'react'
import { Redirect, useLocation, useRouteMatch } from 'react-router'
import Permissions from 'types/Permissions'
import { isUndefinedOrNull } from 'util/checks'
import { grupoCboVisualizarAcompanhamentos } from 'view/cidadao/acessos'

import { usePeriodoGestacional } from '../../soap/aside/pre-natal/hooks/usePeriodoGestacional'
import { getDumAtual } from '../../soap/aside/pre-natal/util/util-acompanhamentoPreNatal'
import { InformacoesGestacaoTabContent } from './components/InformacoesGestacaoTabContent'
import { MedicoesGestacaoTabContent } from './components/MedicoesGestacaoTabContent'

const INFO_GESTACAO_URL = 'informacoes'
const MEDICOES_GESTACAO_URL = 'medicoes'

interface AcompanhamentoPreNatalTabsProps {
  cidadaoId: ID
  prontuarioId: ID
  atendimentoId: ID
  dataReferencia: Instant
  vacinacaoPath: string
}

export const AcompanhamentoPreNatalTabs = (props: AcompanhamentoPreNatalTabsProps) => {
  const { cidadaoId, prontuarioId, atendimentoId, dataReferencia, vacinacaoPath } = props
  const match = useRouteMatch()
  const location = useLocation()

  const informacoesPath = `${match.url}/${INFO_GESTACAO_URL}${location.search}`
  const medicoesPath = `${match.url}/${MEDICOES_GESTACAO_URL}${location.search}`

  const {
    data: { atendimentosProfUltimaGestacao },
    loading: loadingAtendimentos,
  } = useAcompanhamentoPreNatalQuery({
    variables: {
      input: prontuarioId,
    },
    skip: !prontuarioId,
    fetchPolicy: 'cache-first',
  })

  const atendimentosProfissionaisUltimaGestacao = atendimentosProfUltimaGestacao as AtendimentoProfissional[]
  const dum = getDumAtual(atendimentosProfissionaisUltimaGestacao)

  const {
    loading: loadingPeriodoGestacional,
    idadeGestacional,
    dataProvavelPartoEcografica: dppEcografica,
    dataInicioGestacao,
  } = usePeriodoGestacional({
    prontuarioId: prontuarioId,
    dum,
    dataAtendimento: dataReferencia,
  })

  const renderInformacoesGestacao = () => (
    <InformacoesGestacaoTabContent
      cidadaoId={cidadaoId}
      prontuarioId={prontuarioId}
      atendimentoId={atendimentoId}
      dataReferencia={dataReferencia}
      loading={loadingAtendimentos || loadingPeriodoGestacional}
      dum={dum}
      idadeGestacional={idadeGestacional}
      dataInicioGestacao={dataInicioGestacao}
      dppEcografica={dppEcografica}
      quantidadeConsultas={atendimentosProfissionaisUltimaGestacao?.length}
      dataPrimeiraConsulta={atendimentosProfissionaisUltimaGestacao?.first()?.iniciadoEm}
      dataUltimaConsulta={atendimentosProfissionaisUltimaGestacao?.last()?.iniciadoEm}
      atendimentosProfUltimaGestacao={atendimentosProfissionaisUltimaGestacao}
      medicoesPath={medicoesPath}
      vacinacaoPath={vacinacaoPath}
    />
  )

  const renderMedicoes = () => (
    <MedicoesGestacaoTabContent
      cidadaoId={cidadaoId}
      prontuarioId={prontuarioId}
      dataInicioGestacao={dataInicioGestacao}
      atendimentosProfUltimaGestacao={atendimentosProfissionaisUltimaGestacao}
      isDataInicioGestacaoEcografica={!isUndefinedOrNull(idadeGestacional?.ecograficaEmDias)}
      loading={loadingAtendimentos || loadingPeriodoGestacional}
    />
  )

  return (
    <Fragment>
      <Tabs style={styles.tabs}>
        <TabLink to={informacoesPath}>Informações da gestação</TabLink>
        <TabLink to={medicoesPath}>Medições</TabLink>
      </Tabs>
      <PecSwitch>
        <Redirect exact from={match.url} to={informacoesPath} />
        <PrivateRoute
          path={`${match.url}/${INFO_GESTACAO_URL}`}
          render={renderInformacoesGestacao}
          permission={Permissions.visualizarProntuario}
          cbos={grupoCboVisualizarAcompanhamentos}
        />
        <PrivateRoute
          path={`${match.url}/${MEDICOES_GESTACAO_URL}`}
          render={renderMedicoes}
          permission={Permissions.visualizarProntuario}
          cbos={grupoCboVisualizarAcompanhamentos}
        />
      </PecSwitch>
    </Fragment>
  )
}

const styles = {
  tabs: css`
    margin-bottom: 1rem;
  `,
}
